/**
 * Plain-text copy of the competition lock badge (Bez, 2026-10-09: "locked LPs but the card still says locked elsewhere"). A competition counts ONE
 * pool, so locks in another fee tier, in another pool or of a farmed position do not count. The copy names that pool and its locked share.
 * Data: the competition's pool, its token symbols and fee (read on-chain with the pool) and the API's liquidityLock fields only.
 */
export type LockFacts = { status: string; shareBps?: number; until?: number | null; kind?: 'v2' | 'v3' | null }
export type PairFacts = { symbol0?: string; symbol1?: string; fee?: number }

/** The Lock create flow with this pool preselected: the wallet's positions in it first (one is preselected), or its V2 LP token. */
export const lockCreateHref = (pool?: string) => (pool ? `/cadinu-lock/create?pool=${pool}` : '/cadinu-lock/create')

export const ZERO_LINE = 'Only liquidity in this pool counts. Positions in a farm cannot be locked; unstake first.'

const shortAddr = (a: string) => (a && a.length > 12 ? `${a.slice(0, 6)}…${a.slice(-4)}` : a)
const day = (t: number) => new Date(t * 1000).toISOString().slice(0, 10)
export const sharePct = (bps?: number) => `${Math.round((bps ?? 0) / 100)} %`

/** A V3 fee in hundredths of a bip: 500 -> "0.05 %", 30000 -> "3 %". V2 pools have none. */
export function feeLabel(fee?: number) {
  if (typeof fee !== 'number' || !Number.isFinite(fee) || fee <= 0) return ''
  return `${Number((fee / 10000).toFixed(4))} %`
}

/** "CADINU/WBNB 0.05 % · 0xfbd4…1234"; "Pool 0xfbd4…1234" until the symbols are read. */
export function poolName(pool: string, pair: PairFacts) {
  const fee = feeLabel(pair.fee)
  if (!pair.symbol0 || !pair.symbol1) return `Pool ${shortAddr(pool)}`
  return `${pair.symbol0}/${pair.symbol1}${fee ? ` ${fee}` : ''} · ${shortAddr(pool)}`
}

/** One line: the share of the counted pool in CADINU Lock, the until-date, and what the tick needs. */
export function lockLine(lock: LockFacts, pool: string, pair: PairFacts) {
  const name = poolName(pool, pair)
  const until = lock.until ? ` until ${day(lock.until)}` : ''
  if (lock.status === 'locked') return `${sharePct(lock.shareBps)} of ${name} is in CADINU Lock${until}. Checked on-chain.`
  if (lock.status === 'partial') return `${sharePct(lock.shareBps)} of ${name} is in CADINU Lock${until}. The tick needs 50 %.`
  return `0 % of ${name} is in CADINU Lock.`
}
